import React, { useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Webcam from "react-webcam";
import axios from "axios";
import { checkLivenessSession, createLivenessSession } from "../../utils/queries";
import { attendanceService } from "../../services/attendanceService";

const FaceAuth = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const webcamRef = useRef(null);
  const [submit, setSubmit] = useState(false);
  const [showOutdoorModal, setShowOutdoorModal] = useState(false);
  const [outdoorReason, setOutdoorReason] = useState("");
  const [location, setLocation] = useState(null);
  const [attendanceList, setAttendanceList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  const empCode = userInfo && userInfo[1]?.InfoField;
  const officeLocation = { lat: 28.50894, lon: 77.23227 };

  useEffect(() => {
    fetchTodayAttendance();
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      setLoading(true);
      const today = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
      console.log('Fetching attendance for:', today, 'Employee:', empCode);
      
      const response = await axios.post(
        'https://payrollnodbodemo.magnussoftech.in/api/GetEmployeeDailyAttList',
        {
          title: 'GetEmployeeDailyAttList',
          description: 'Fetch attendance list',
          ReqAttDate: today,
          ReqEmpCode: empCode,
          ReqCompanyCode: '1'
        },
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          timeout: 10000
        }
      );

      console.log('Attendance API Response:', response.data);

      if (response.data && response.data[0]?.JSONData1) {
        const data = JSON.parse(response.data[0].JSONData1);
        setAttendanceList(data);
      } else {
        setAttendanceList([]);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error(error.response?.data?.message || "Failed to fetch attendance");
      setAttendanceList([]);
    } finally {
      setLoading(false);
    }
  };

  const getLivenessSession = async () => {
    try {
      console.log('Creating liveness session...');
      const response = await checkLivenessSession();
      console.log('Liveness session response:', response);
      
      const data = response[0];
      if (data.status === 200) {
        return data.InfoField1;
      } else {
        toast.warn(data.message || "Failed to create liveness session");
        return null;
      }
    } catch (error) {
      console.error("Error creating liveness session:", error);
      toast.error("Liveness session failed: " + (error.message || "Unknown error"));
      return null;
    }
  };

  const captureVideo = async () => {
    if (!webcamRef.current) {
      toast.error("Camera not initialized");
      return;
    }

    setSubmit(true);
    try {
      // Step 1: Capture image and convert to base64 (like Flutter's pickImage)
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        throw new Error("Failed to capture image");
      }

      // Get current location (like Flutter's getCurrentLocation)
      const currentLocation = await attendanceService.getCurrentLocation();
      setLocation(currentLocation);

      // Calculate distance (like Flutter's getDistanceInMeters)
      const distance = attendanceService.calculateDistance(
        officeLocation.lat,
        officeLocation.lon,
        currentLocation.latitude,
        currentLocation.longitude
      );

      // Check if outdoor attendance is needed
      if (distance > 800) {
        setShowOutdoorModal(true);
        return;
      }

      // Format date exactly like Flutter
      const now = new Date();
      const date = now.toLocaleDateString('en-GB').replace(/\//g, '-');
      const dateTime = now.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/\//g, '-') + '.000';

      // Get base64 data (like Flutter's base64Encode)
      const base64Data = imageSrc.split(',')[1];

      // Step 2: User Verification (First API call)
      const verifyParams = new URLSearchParams();
      verifyParams.append('title', 'UserVerify');
      verifyParams.append('description', 'User Login');
      verifyParams.append('faceId', 'OiHULFjb');
      verifyParams.append('ReqAttDate', date);
      verifyParams.append('ReqDateNTime', dateTime);
      verifyParams.append('ReqEmpCode', empCode);
      verifyParams.append('image', base64Data);
      verifyParams.append('ReqAttLocation', `${currentLocation.latitude},${currentLocation.longitude}`);
      verifyParams.append('ReqCompanyCode', '1');

      console.log('Step 1 - User Verification Request:', {
        title: 'UserVerify',
        description: 'User Login',
        faceId: 'OiHULFjb',
        ReqAttDate: date,
        ReqDateNTime: dateTime,
        ReqEmpCode: empCode,
        ReqAttLocation: `${currentLocation.latitude},${currentLocation.longitude}`,
        ReqCompanyCode: '1',
        imageLength: base64Data.length
      });

      const verifyResponse = await axios.post(
        'https://payrollnodbodemo.magnussoftech.in/api/UserVerify',
        verifyParams,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          timeout: 30000
        }
      );

      console.log('User Verification Response:', verifyResponse.data);

      if (verifyResponse.data[0]?.status !== 200) {
        throw new Error(verifyResponse.data[0]?.message || "User verification failed");
      }

      // Get image path from verification response
      const imagePath = verifyResponse.data[0]?.InfoField3;
      if (!imagePath) {
        throw new Error("No image path received from verification");
      }

      // Step 3: Update Daily Entry (Second API call)
      const updateParams = new URLSearchParams();
      updateParams.append('title', 'UpdateDailyInOutEntry');
      updateParams.append('description', 'Updating Daily In and Out');
      updateParams.append('ReqAttDate', date);
      updateParams.append('ReqDateNTime', dateTime);
      updateParams.append('ReqEmpCode', empCode);
      updateParams.append('ReqAttImage', imagePath);
      updateParams.append('ReqAttLocation', `${currentLocation.latitude},${currentLocation.longitude}`);
      updateParams.append('ReqCompanyCode', '1');
      updateParams.append('ReqRangeType', 'Indoor');
      updateParams.append('ReqOutDoorReason', '');

      console.log('Step 2 - Update Daily Entry Request:', {
        title: 'UpdateDailyInOutEntry',
        description: 'Updating Daily In and Out',
        ReqAttDate: date,
        ReqDateNTime: dateTime,
        ReqEmpCode: empCode,
        ReqAttImage: imagePath,
        ReqAttLocation: `${currentLocation.latitude},${currentLocation.longitude}`,
        ReqCompanyCode: '1',
        ReqRangeType: 'Indoor',
        ReqOutDoorReason: ''
      });

      const updateResponse = await axios.post(
        'https://payrollnodbodemo.magnussoftech.in/api/UpdateDailyInOutEntry',
        updateParams,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          timeout: 30000
        }
      );

      console.log('Update Daily Entry Response:', updateResponse.data);

      if (updateResponse.data[0]?.status === 200) {
        toast.success(updateResponse.data[0]?.message || "Attendance marked successfully");
        fetchTodayAttendance();
      } else {
        throw new Error(updateResponse.data[0]?.message || "Failed to mark attendance");
      }
    } catch (error) {
      console.error('Error in attendance process:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      }
      toast.error(error.message || "Failed to mark attendance");
    } finally {
      setSubmit(false);
    }
  };

  const handleOutdoorSubmit = async () => {
    if (!outdoorReason.trim()) {
      toast.error("Please provide a reason for outdoor attendance");
      return;
    }

    try {
      setSubmit(true);
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        throw new Error("Failed to capture image");
      }

      // Format date exactly like Flutter
      const now = new Date();
      const date = now.toLocaleDateString('en-GB').replace(/\//g, '-');
      const dateTime = now.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/\//g, '-') + '.000';

      // Get base64 data
      const base64Data = imageSrc.split(',')[1];

      // Step 1: User Verification
      const verifyParams = new URLSearchParams();
      verifyParams.append('title', 'UserVerify');
      verifyParams.append('description', 'User Login');
      verifyParams.append('faceId', 'OiHULFjb');
      verifyParams.append('ReqAttDate', date);
      verifyParams.append('ReqDateNTime', dateTime);
      verifyParams.append('ReqEmpCode', empCode);
      verifyParams.append('image', base64Data);
      verifyParams.append('ReqAttLocation', `${location.latitude},${location.longitude}`);
      verifyParams.append('ReqCompanyCode', '1');

      const verifyResponse = await axios.post(
        'https://payrollnodbodemo.magnussoftech.in/api/UserVerify',
        verifyParams,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          timeout: 30000
        }
      );

      if (verifyResponse.data[0]?.status !== 200) {
        throw new Error(verifyResponse.data[0]?.message || "User verification failed");
      }

      const imagePath = verifyResponse.data[0]?.InfoField3;
      if (!imagePath) {
        throw new Error("No image path received from verification");
      }

      // Step 2: Update Daily Entry
      const updateParams = new URLSearchParams();
      updateParams.append('title', 'UpdateDailyInOutEntry');
      updateParams.append('description', 'Updating Daily In and Out');
      updateParams.append('ReqAttDate', date);
      updateParams.append('ReqDateNTime', dateTime);
      updateParams.append('ReqEmpCode', empCode);
      updateParams.append('ReqAttImage', imagePath);
      updateParams.append('ReqAttLocation', `${location.latitude},${location.longitude}`);
      updateParams.append('ReqCompanyCode', '1');
      updateParams.append('ReqRangeType', 'Outdoor');
      updateParams.append('ReqOutDoorReason', outdoorReason);

      const updateResponse = await axios.post(
        'https://payrollnodbodemo.magnussoftech.in/api/UpdateDailyInOutEntry',
        updateParams,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          timeout: 30000
        }
      );

      if (updateResponse.data[0]?.status === 200) {
        toast.success(updateResponse.data[0]?.message || "Attendance marked successfully");
        setShowOutdoorModal(false);
        setOutdoorReason("");
        fetchTodayAttendance();
      } else {
        throw new Error(updateResponse.data[0]?.message || "Failed to mark attendance");
      }
    } catch (error) {
      console.error('Error in outdoor attendance:', error);
      toast.error(error.message || "Failed to mark outdoor attendance");
    } finally {
      setSubmit(false);
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  return (
    <div className="container-fluid px-4 py-3">
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb bg-transparent p-0">
          <li className="breadcrumb-item">
            <Link to="/dashboard" className="text-decoration-none">Dashboard</Link>
          </li>
          <li className="breadcrumb-item active">Face Authentication</li>
        </ol>
      </nav>

      <div className="row g-4">
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-primary text-white">
              <h5 className="card-title mb-0">Mark Attendance</h5>
            </div>
            <div className="card-body d-flex flex-column">
              <div className="webcam-container position-relative mb-3">
                <Webcam
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  videoConstraints={{
                    width: 640,
                    height: 480,
                    facingMode: "user"
                  }}
                  className="img-fluid rounded w-100"
                  style={{ maxHeight: '400px', objectFit: 'cover' }}
                />
                <div className="position-absolute top-0 start-0 m-3">
                  <span className="badge bg-success">Live</span>
                </div>
              </div>
              <div className="mt-auto text-center">
                <button
                  className="btn btn-primary btn-lg px-5"
                  onClick={captureVideo}
                  disabled={submit}
                >
                  {submit ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-camera me-2"></i>
                      MARK ATTENDANCE
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Today's Attendance</h5>
              <button 
                className="btn btn-light btn-sm"
                onClick={fetchTodayAttendance}
                disabled={loading}
              >
                <i className="fas fa-sync-alt"></i>
              </button>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : attendanceList.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
                  <p className="text-muted">No attendance records found</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-primary">
                      <tr>
                        <th className="text-white">S.no.</th>
                        <th className="text-white">Staff ID</th>
                        <th className="text-white">Name</th>
                        <th className="text-white">Type</th>
                        <th className="text-white">Entry Date</th>
                        <th className="text-white">Entry Time</th>
                        <th className="text-white">Range Type</th>
                        <th className="text-white">Role</th>
                        <th className="text-white">Photo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceList.map((record, index) => (
                        <tr key={index}>
                          <td className="text-center">{index + 1}</td>
                          <td className="text-center">{record.EMPCODE || "N/A"}</td>
                          <td className="text-center">{record.EmployeeName || "N/A"}</td>
                          <td className="text-center">
                            <span className={`badge ${record.AttType === 'IN' ? 'bg-success' : 'bg-danger'}`}>
                              {record.AttType || "Unknown"}
                            </span>
                          </td>
                          <td className="text-center">
                            {new Date(record.EntryTime).toLocaleDateString('en-GB')}
                          </td>
                          <td className="text-center">
                            {new Date(record.EntryTime).toLocaleTimeString()}
                          </td>
                          <td className="text-center">
                            <span className={`badge ${record.RangeType === 'Indoor' ? 'bg-info' : 'bg-warning'}`}>
                              {record.RangeType || "Unknown"}
                            </span>
                          </td>
                          <td className="text-center">{record.Designation || "Unknown"}</td>
                          <td className="text-center">
                            {record.AttImage ? (
                              <img
                                src={`https://payrollnodbodemo.magnussoftech.in/${record.AttImage}`}
                                alt="Attendance"
                                className="img-thumbnail rounded-circle"
                                style={{ width: '50px', height: '50px', objectFit: 'cover', cursor: 'pointer' }}
                                onClick={() => handleImageClick(`https://payrollnodbodemo.magnussoftech.in/${record.AttImage}`)}
                              />
                            ) : (
                              <i className="fas fa-user-circle" style={{ fontSize: '50px', color: '#ccc' }}></i>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="modal-backdrop show" style={{ display: 'block' }}>
          <div className="modal" style={{ display: 'block' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">Attendance Photo</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={closeImageModal}
                  ></button>
                </div>
                <div className="modal-body text-center p-0">
                  <img
                    src={selectedImage}
                    alt="Attendance"
                    className="img-fluid"
                    style={{ maxHeight: '80vh' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Outdoor Attendance Modal */}
      {showOutdoorModal && (
        <div className="modal-backdrop show" style={{ display: 'block' }}>
          <div className="modal" style={{ display: 'block' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">Outdoor Attendance</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setShowOutdoorModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="outdoorReason" className="form-label">
                      Reason for outdoor attendance
                    </label>
                    <textarea
                      id="outdoorReason"
                      className="form-control"
                      rows={3}
                      value={outdoorReason}
                      onChange={(e) => setOutdoorReason(e.target.value)}
                      placeholder="Please provide a reason for outdoor attendance"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowOutdoorModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleOutdoorSubmit}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaceAuth;
